import { createContext, useContext, useState, useMemo } from "react";
import latestResults from "../data/results.json";
import archive from "../data/results_archive.json";

const ResultsContext = createContext(null);

/**
 * Provides the active results dataset to the component tree.
 * - "latest" always refers to results.json (the current live data)
 * - Any archived run_id shows that snapshot instead
 */
export function ResultsProvider({ children }) {
  const [activeRunId, setActiveRunId] = useState("latest");

  const runs = useMemo(() => {
    const list = archive.map((a) => ({
      id: a.run_id,
      date: a.run_date,
      label: a.run_date.split('-').reverse().join('-'), // e.g., 18-04-2026
    }));
    // Ensure "latest" is always at the top and never duplicated
    const latestDate = latestResults.meta?.run_date || "Unknown";
    const latestExists = list.some((r) => r.date === latestDate);
    return [
      { id: "latest", date: latestDate, label: `Latest · ${latestDate}` },
      ...list.filter((r) => !latestExists || r.date !== latestDate),
    ];
  }, []);

  const results = useMemo(() => {
    if (activeRunId === "latest") return latestResults;
    const snap = archive.find((a) => a.run_id === activeRunId);
    return snap ? snap.data : latestResults;
  }, [activeRunId]);

  const isArchive = activeRunId !== "latest";

  return (
    <ResultsContext.Provider value={{ results, runs, activeRunId, setActiveRunId, isArchive }}>
      {children}
    </ResultsContext.Provider>
  );
}

export function useResults() {
  const ctx = useContext(ResultsContext);
  if (!ctx) throw new Error("useResults must be used inside <ResultsProvider>");
  return ctx;
}
